import { Link } from 'react-router-dom';
import { TabHeader } from '../components/TabHeader';
import { TabSearchStrip } from '../components/TabSearchStrip';
import { Footer } from '../components/Footer';
import { FAQ, FaqEntry } from '../data/pricing-data';

/**
 * Dedicated FAQ tab — hosts Appendix D's 20 questions. Previously
 * embedded in /pricing; promoted to its own route so it\'s reachable
 * from the home tile grid + Explore dropdown, and so prospects find
 * it without having to open the pricing page first.
 */
export function FaqPage(): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-sovereign-900">
      <TabHeader />

      <main className="flex-1">
        <section className="border-b border-sovereign-line bg-brand-purple-dark px-4 py-10 text-white md:px-8 md:py-14">
          <div className="mx-auto max-w-3xl">
            <nav aria-label="Breadcrumb" className="mb-3 text-xs text-white/70">
              <Link to="/" className="hover:text-white hover:underline">
                Home
              </Link>
              <span className="mx-2" aria-hidden>·</span>
              <span>FAQ</span>
            </nav>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">Frequently asked questions</h1>
            <p className="max-w-2xl text-base leading-relaxed text-white/85">
              The twenty questions that come up first in pilot conversations, answered.
              Anything not covered here — use the search box at the bottom of this page,
              or reach out to{' '}
              <a href="mailto:sales@dideck.com" className="underline hover:text-white">
                sales@dideck.com
              </a>
              .
            </p>
          </div>
        </section>

        <section className="px-4 py-10 md:px-8 md:py-14">
          <div className="mx-auto max-w-3xl space-y-2">
            {FAQ.map((f, i) => (
              <FaqRow key={i} entry={f} defaultOpen={i === 0} />
            ))}
          </div>
        </section>
      </main>

      <TabSearchStrip
        slug="faq"
        topic="anything about DI Deck"
        chips={[
          'Which regulated sectors are you piloting with today?',
          'What data leaves my infrastructure by default?',
          'How much of the platform is open-source?',
        ]}
      />
      <Footer />
    </div>
  );
}

function FaqRow({
  entry,
  defaultOpen,
}: {
  entry: FaqEntry;
  defaultOpen: boolean;
}): JSX.Element {
  return (
    <details
      className="group rounded-md border border-sovereign-line bg-white shadow-card"
      open={defaultOpen}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-4 px-4 py-3 text-sm font-semibold text-ink hover:text-brand-purple">
        <span>{entry.q}</span>
        <span
          className="text-brand-purple transition-transform group-open:rotate-45"
          aria-hidden
        >
          +
        </span>
      </summary>
      <div className="border-t border-sovereign-line px-4 py-3 text-sm leading-relaxed text-ink-mute">
        {entry.a}
      </div>
    </details>
  );
}
